/**
 * Default group strip (compact row) + How it works hints for AI per-drop popovers.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useId } from 'react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { PromptGroup } from '@/features/campaign-texts/types/campaign-texts.types';

const SELECT_TITLE =
  'Randomize updates each row immediately (all groups or only under a folder). Apply to all copies this default to every row.';

export interface AiPerDropDefaultGroupPanelProps {
  masterId: string;
  groupId: string;
  onGroupIdChange: (id: string) => void;
  activeGroups: PromptGroup[];
  /** Optional hierarchical label (e.g. Parent / Child) for nested text groups. */
  groupLabelLookup?: (g: PromptGroup) => string;
  showApplyToAll: boolean;
  onApplyToAll: () => void;
}

export function AiPerDropDefaultGroupPanel({
  masterId,
  groupId,
  onGroupIdChange,
  activeGroups,
  groupLabelLookup,
  showApplyToAll,
  onApplyToAll,
}: AiPerDropDefaultGroupPanelProps) {
  const selectId = useId();
  return (
    <div className="space-y-2">
      <div className="rounded-md border border-border/70 bg-muted/15 px-2 py-1">
        <div
          className={cn(
            'flex flex-col gap-1.5',
            showApplyToAll ? 'sm:flex-row sm:items-center sm:gap-2' : 'sm:flex-row sm:items-center',
          )}
        >
          <div className="flex min-h-8 min-w-0 flex-1 items-center gap-2">
            <label
              htmlFor={selectId}
              className="shrink-0 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Default group
            </label>
            <select
              id={selectId}
              title={SELECT_TITLE}
              className={cn(
                'h-8 min-w-0 flex-1 rounded border border-input bg-background px-2 text-xs',
                'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              value={groupId}
              onChange={(e) => onGroupIdChange(e.target.value)}
            >
              <option value={masterId}>Default</option>
              {activeGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {groupLabelLookup ? groupLabelLookup(g) : g.name}
                </option>
              ))}
            </select>
          </div>
          {showApplyToAll ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-full px-2.5 text-[11px] font-medium sm:w-auto sm:shrink-0"
              title="Set every row to this default group"
              onClick={onApplyToAll}
            >
              Apply to all
            </Button>
          ) : null}
        </div>
      </div>
      <div className="border-t border-border/60 pt-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/90">
          How it works
        </p>
        <ul className="mt-1.5 list-inside list-disc space-y-1 text-[10px] leading-relaxed text-muted-foreground marker:text-muted-foreground/70">
          <li>
            <span className="font-medium text-foreground/85">Randomize all / Randomize in folder</span> runs
            immediately on each row below; use the scope menu to limit picks to leaves under one folder.
          </li>
          <li>
            <span className="font-medium text-foreground/85">Apply to all</span> sets every row to the group
            selected above.
          </li>
        </ul>
      </div>
    </div>
  );
}
